/**
 * Heart Rate Sessions History — shows every Web-Bluetooth HR session
 * the user has ended & saved. Renders lifetime totals, a BPM trend
 * line chart, and a scrollable list of individual sessions.
 *
 * Source of truth: GET /api/wearables/hr-sessions (newest → oldest)
 */
import { useEffect, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { format, parseISO } from 'date-fns';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from 'recharts';
import { HeartPulse, Flame, Clock, Zap, Bluetooth, TrendingUp, Award, Dumbbell } from 'lucide-react';
import { NavBar } from '../components/NavBar';
import { useAuth } from '../context/AuthContext';
import { HeartRateWidget } from '../components/workout-detail/HeartRateWidget';

const formatDuration = (sec) => {
  const totalMin = Math.floor(sec / 60);
  const s = sec % 60;
  if (totalMin < 60) return `${totalMin}m ${s}s`;
  const h = Math.floor(totalMin / 60);
  const m = totalMin % 60;
  return `${h}h ${m}m`;
};

const StatCard = ({ icon: Icon, label, value, unit, accent = 'text-primary', delay = 0 }) => (
  <motion.div
    initial={{ opacity: 0, y: 20 }}
    animate={{ opacity: 1, y: 0 }}
    transition={{ delay }}
    className="bg-card border border-border rounded-2xl p-6"
  >
    <div className="flex items-center justify-between mb-2">
      <Icon className={`h-6 w-6 ${accent}`} />
      <span className="text-xs text-muted-foreground uppercase tracking-widest">{label}</span>
    </div>
    <p className={`font-heading text-3xl ${accent}`}>{value ?? '—'}</p>
    <p className="text-muted-foreground text-sm">{unit}</p>
  </motion.div>
);

export const HRSessionsPage = () => {
  const { api } = useAuth();
  const [loading, setLoading] = useState(true);
  const [sessions, setSessions] = useState([]);
  const [totals, setTotals] = useState({ sessions: 0, total_minutes: 0, total_calories: 0, max_bpm_ever: 0 });

  const load = useCallback(async () => {
    try {
      const res = await api().get('/wearables/hr-sessions?limit=100');
      setSessions(res.data?.sessions || []);
      setTotals(res.data?.totals || {});
    } catch (e) {
      // silent — page still renders the empty state
    }
    setLoading(false);
  }, [api]);

  useEffect(() => {
    load();
  }, [load]);

  // Chart data — sessions are returned newest first, chart wants oldest first
  const chartData = [...sessions].reverse().map((s) => ({
    date: format(parseISO(s.created_at), 'MMM d'),
    avg: s.avg_bpm,
    max: s.max_bpm,
    calories: s.calories_estimated,
  }));

  return (
    <div className="min-h-screen bg-black" data-testid="hr-sessions-page">
      <NavBar title="Heart Rate" />

      <main className="container mx-auto px-6 py-8 max-w-5xl">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-10"
        >
          <div className="flex items-center gap-3 mb-2">
            <HeartPulse className="h-8 w-8 text-red-400" />
            <h2 className="font-heading text-4xl md:text-5xl text-white uppercase tracking-wider">
              Heart Rate <span className="text-primary">Sessions</span>
            </h2>
          </div>
          <p className="text-muted-foreground text-lg">
            Live BPM and calorie history from every wearable session you&apos;ve saved.
          </p>
        </motion.div>

        {/* Free-Session Connect Card — lets users pair a device directly
            from this page without needing to start a workout first. */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ delay: 0.05 }}
          className="bg-gradient-to-br from-red-500/15 via-card to-card border border-red-500/30 rounded-2xl p-6 mb-8"
          data-testid="hr-free-session-card"
        >
          <div className="flex items-start gap-4">
            <div className="h-11 w-11 shrink-0 rounded-full bg-red-500/20 border border-red-500/40 flex items-center justify-center">
              <Bluetooth className="h-5 w-5 text-red-300" />
            </div>
            <div className="flex-1 min-w-0">
              <h3 className="font-heading text-lg text-white uppercase tracking-wider mb-1">
                Free Session
              </h3>
              <p className="text-muted-foreground text-sm leading-relaxed">
                Pair a heart-rate monitor right here — no workout required. Use
                for warm-ups, walks, cardio machines, or any time you just want
                to track BPM &amp; calories on the fly.
              </p>
            </div>
          </div>
          <HeartRateWidget workoutTitle="Free Session" onSaved={load} />
        </motion.div>

        {/* Lifetime Totals */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <StatCard icon={Award} label="Sessions" value={totals.sessions} unit="saved" delay={0.05} />
          <StatCard icon={Clock} label="Total Time" value={totals.total_minutes} unit="minutes" delay={0.1} />
          <StatCard icon={Flame} label="Calories" value={totals.total_calories?.toLocaleString?.() ?? totals.total_calories} unit="kcal burned" accent="text-red-400" delay={0.15} />
          <StatCard icon={TrendingUp} label="Peak BPM" value={totals.max_bpm_ever} unit="max ever" accent="text-red-400" delay={0.2} />
        </div>

        {loading ? (
          <div className="py-20 text-center text-muted-foreground">Loading…</div>
        ) : sessions.length === 0 ? (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            className="bg-card border border-border p-10 text-center"
            data-testid="hr-empty-state"
          >
            <Bluetooth className="h-14 w-14 text-primary/60 mx-auto mb-4" />
            <h3 className="font-heading text-xl text-white uppercase tracking-wider mb-2">
              No sessions yet
            </h3>
            <p className="text-muted-foreground max-w-md mx-auto leading-relaxed">
              Tap <span className="text-white font-medium">Connect Heart Rate</span> above
              to pair your BLE strap or smartwatch (Chrome/Edge desktop or Android),
              then hit <span className="text-white font-medium">End &amp; Save</span> to
              log your first session. You can also connect from inside any workout.
            </p>
          </motion.div>
        ) : (
          <>
            {/* Trend chart */}
            {sessions.length >= 2 && (
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ delay: 0.25 }}
                className="bg-card border border-border rounded-2xl p-6 mb-8"
                data-testid="hr-trend-chart"
              >
                <div className="flex items-center gap-2 mb-4">
                  <TrendingUp className="h-5 w-5 text-primary" />
                  <h3 className="font-heading text-lg text-white uppercase tracking-wider">
                    BPM Trend
                  </h3>
                </div>
                <div className="h-64">
                  <ResponsiveContainer width="100%" height="100%">
                    <LineChart data={chartData} margin={{ top: 10, right: 12, left: -12, bottom: 0 }}>
                      <CartesianGrid strokeDasharray="3 3" stroke="#27272a" />
                      <XAxis dataKey="date" stroke="#a1a1aa" tick={{ fontSize: 11 }} />
                      <YAxis stroke="#a1a1aa" tick={{ fontSize: 11 }} domain={['dataMin - 5', 'dataMax + 5']} />
                      <Tooltip
                        contentStyle={{
                          backgroundColor: '#09090b',
                          border: '1px solid #d4af37',
                          borderRadius: '4px',
                          color: '#fff',
                          fontSize: '12px',
                        }}
                        labelStyle={{ color: '#d4af37', textTransform: 'uppercase', letterSpacing: '0.1em' }}
                      />
                      <Line type="monotone" dataKey="avg" name="Avg BPM" stroke="#d4af37" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                      <Line type="monotone" dataKey="max" name="Max BPM" stroke="#f87171" strokeWidth={2} dot={{ r: 3 }} activeDot={{ r: 5 }} />
                    </LineChart>
                  </ResponsiveContainer>
                </div>
              </motion.div>
            )}

            {/* Session list */}
            <motion.div
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.3 }}
            >
              <h3 className="font-heading text-lg text-white uppercase tracking-wider mb-4">
                Recent Sessions
              </h3>
              <div className="space-y-3" data-testid="hr-sessions-list">
                {sessions.map((s, i) => (
                  <motion.div
                    key={s.id}
                    initial={{ opacity: 0, x: -12 }}
                    animate={{ opacity: 1, x: 0 }}
                    transition={{ delay: 0.05 * Math.min(i, 8) }}
                    className="bg-card border border-border hover:border-primary/40 rounded-2xl p-4 transition-colors"
                    data-testid={`hr-session-${s.id}`}
                  >
                    <div className="flex items-start justify-between gap-4 mb-3">
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2 mb-1">
                          <HeartPulse className="h-4 w-4 text-red-400 shrink-0" />
                          <p className="font-heading text-white uppercase tracking-wider text-sm truncate">
                            {s.workout_title || 'Free Session'}
                          </p>
                        </div>
                        <div className="flex flex-wrap items-center gap-x-3 gap-y-0.5 text-xs text-muted-foreground">
                          <span>{format(parseISO(s.created_at), 'MMM d, yyyy · h:mm a')}</span>
                          {s.device_name && (
                            <span className="flex items-center gap-1">
                              <Bluetooth className="h-3 w-3" />
                              {s.device_name}
                            </span>
                          )}
                        </div>
                      </div>
                      <div className="text-right shrink-0">
                        <p className="text-[10px] uppercase tracking-widest text-muted-foreground font-heading">Duration</p>
                        <p className="font-heading text-white text-lg tabular-nums">
                          {formatDuration(s.duration_seconds)}
                        </p>
                      </div>
                    </div>

                    <div className="grid grid-cols-3 gap-2">
                      <div className="bg-black/40 border border-white/10 rounded-md p-2 text-center">
                        <p className="text-[9px] uppercase tracking-widest text-muted-foreground font-heading">Avg</p>
                        <p className="font-heading text-primary text-lg">{s.avg_bpm}</p>
                      </div>
                      <div className="bg-black/40 border border-white/10 rounded-md p-2 text-center">
                        <p className="text-[9px] uppercase tracking-widest text-muted-foreground font-heading">Max</p>
                        <p className="font-heading text-red-300 text-lg">{s.max_bpm}</p>
                      </div>
                      <div className="bg-black/40 border border-white/10 rounded-md p-2 text-center">
                        <p className="text-[9px] uppercase tracking-widest text-muted-foreground font-heading flex items-center justify-center gap-1">
                          <Zap className="h-2.5 w-2.5" /> kcal
                        </p>
                        <p className="font-heading text-orange-300 text-lg">{s.calories_estimated}</p>
                      </div>
                    </div>
                  </motion.div>
                ))}
              </div>
            </motion.div>
          </>
        )}
      </main>
    </div>
  );
};

export default HRSessionsPage;
